import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../networking/client';
import { RootState } from '../store';
import { toast } from 'react-toastify';
import { fetchSalesOrders } from './salesOrders';

export interface ConsultationNote {
  consultation_notes_id: number;
  consultation_notes_name: string;
  consultation_notes_description: string;
  service_id: number;
}

export interface ConsultationNotesState {
  status: string;
  editingStatus: string;
  consultationNotes: ConsultationNote[];
}

const initialState: ConsultationNotesState = {
  status: 'idle',
  editingStatus: 'idle',
  consultationNotes: [],
};

export const fetchConsultationNotes = createAsyncThunk(
  'consultationNotes/fetchAll',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const consultationNotesResponse: any = await client.get('/healthpoint/consultation-notes');
      return consultationNotesResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

type EditConsultationNote = {
  consultationsNoteDescription: string;
  consultationNoteId: number;
};

export const editConsultationNote = createAsyncThunk(
  'consultationNote/edit',
  async (editConsultationNote: EditConsultationNote, { dispatch, rejectWithValue }) => {
    try {
      await client.put(
        `/healthpoint/consultation-note/${editConsultationNote.consultationNoteId}`,
        {
          consultationsNoteDescription: editConsultationNote.consultationsNoteDescription,
        }
      );
      dispatch(fetchConsultationNotes());
      dispatch(fetchSalesOrders());
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const consultationNotesSlice = createSlice({
  name: 'consultationNotes',
  initialState,
  reducers: {
    resetConsultationNotes: (_) => {
      return initialState;
    },
    resetDeletingStatus: (state) => {
      return {
        ...state,
        deletingStatus: 'idle',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConsultationNotes.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchConsultationNotes.fulfilled, (state, action) => {
        state.consultationNotes = action.payload.data.consultationNotes;
        state.status = 'fulfilled';
      })
      .addCase(fetchConsultationNotes.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      })
      .addCase(editConsultationNote.pending, (state) => {
        state.editingStatus = 'pending';
      })
      .addCase(editConsultationNote.fulfilled, (state) => {
        state.editingStatus = 'fulfilled';
        toast.success('Successfully updated the notes.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      })
      .addCase(editConsultationNote.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.editingStatus = 'rejected';
      });
  },
});

export const { resetConsultationNotes, resetDeletingStatus } = consultationNotesSlice.actions;

export default consultationNotesSlice.reducer;

export const selectConsultationNotes = (state: RootState) =>
  state.consultationNotes.consultationNotes;
export const selectConsultationNoteStatus = (state: RootState) => state.consultationNotes.status;
export const selectConsultationNoteEditingStatus = (state: RootState) =>
  state.consultationNotes.editingStatus;
